import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  useGetResourceTypeSchemaQuery,
  useListResourcesByTypeQuery,
} from '../store/hooks';

interface ResourceSchema {
  json_schema?: {
    properties?: Record<string, { title?: string; type?: string }>;
  };
}

interface ResourceListViewProps {
  resourceType: string;
  organizationId?: string;
  patientId?: string;
  providerId?: string;
  page: number;
  perPage: number;
  onPageChange: (page: number) => void;
  // When provided, rows open the resource in-place (org-chrome explorer) instead
  // of routing to the standalone detail page (deep-link surface).
  onOpenResource?: (resourceId: string) => void;
}

// Provenance / tenancy / timestamp columns are noise in a browse table — the
// detail view shows them. `id` is rendered as the link column separately.
const SYSTEM_COLUMNS = new Set([
  'id',
  'scope',
  'organization_id',
  'user_id',
  'created_by_principal_id',
  'created_by_principal_kind',
  'created_at',
  'updated_at',
]);

const MAX_COLUMNS = 6;

function humanize(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function isIsoDate(v: string): boolean {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v);
}

/** Compact single-cell rendering of an arbitrary scalar/complex value. */
function formatCell(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    return isIsoDate(value) ? new Date(value).toLocaleDateString() : value;
  }
  if (Array.isArray(value)) return `[${value.length}]`;
  return '{…}';
}

function rowId(row: Record<string, unknown>): string {
  const id = row.id;
  if (typeof id === 'string') return id;
  if (typeof id === 'number') return String(id);
  return '';
}

/** Pick which columns to show: schema order when available, else row keys. */
function pickColumns(
  items: Record<string, unknown>[],
  schema?: ResourceSchema,
): string[] {
  const fromSchema = Object.keys(schema?.json_schema?.properties ?? {});
  const candidates =
    fromSchema.length > 0
      ? fromSchema
      : Array.from(new Set(items.flatMap(item => Object.keys(item))));
  return candidates
    .filter(key => !SYSTEM_COLUMNS.has(key))
    .slice(0, MAX_COLUMNS);
}

interface ApiErrorData {
  error_code?: string;
  message?: string;
}

function errorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'data' in error) {
    const data = (error as { data?: ApiErrorData }).data;
    if (data?.message) return data.message;
  }
  return 'Could not load this resource list.';
}

/**
 * Generic owner-scoped list for one resource type (GDN-1922). Renders rows from
 * the governed `list` endpoint with schema-driven columns; each row links into
 * the detail view. Filter by passing `patientId` / `providerId` — the backend
 * forwards only the filters that type's `list` accepts.
 */
export function ResourceListView({
  resourceType,
  organizationId,
  patientId,
  providerId,
  page,
  perPage,
  onPageChange,
  onOpenResource,
}: ResourceListViewProps) {
  const { data, isLoading, isFetching, error } = useListResourcesByTypeQuery({
    resourceType,
    organizationId,
    patientId,
    providerId,
    page,
    perPage,
  });
  const { data: schemaData } = useGetResourceTypeSchemaQuery({ resourceType });
  // Route the loosely-typed generated schema through the local interface so
  // nested `json_schema.properties` access stays type-safe (mirrors the
  // detail view).
  const schema: ResourceSchema | undefined = schemaData;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="warning">{errorMessage(error)}</Alert>;
  }

  const rawItems: unknown[] = data?.items ?? [];
  const items: Record<string, unknown>[] = rawItems.map(
    item => (item ?? {}) as Record<string, unknown>,
  );
  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No {resourceType} resources match these filters.
      </Typography>
    );
  }

  const columns = pickColumns(items, schema);
  const titles = schema?.json_schema?.properties ?? {};
  const detailQuery = organizationId
    ? `?organization_id=${organizationId}`
    : '';
  const total = data?.total ?? items.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2">{total} result(s)</Typography>
        {isFetching && <CircularProgress size={14} />}
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>id</TableCell>
            {columns.map(col => (
              <TableCell key={col}>{titles[col]?.title ?? humanize(col)}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((row, i) => {
            const id = rowId(row);
            return (
              <TableRow key={id || i} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {id ? (
                    onOpenResource ? (
                      <MuiLink
                        component="button"
                        type="button"
                        underline="hover"
                        sx={{ fontFamily: 'monospace', fontSize: 12 }}
                        onClick={() => onOpenResource(id)}
                      >
                        {id}
                      </MuiLink>
                    ) : (
                      <Link
                        to={`/admin-v2/resources/${resourceType}/${id}${detailQuery}`}
                      >
                        {id}
                      </Link>
                    )
                  ) : (
                    <Chip size="small" label="no id" />
                  )}
                </TableCell>
                {columns.map(col => (
                  <TableCell key={col}>{formatCell(row[col])}</TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, p) => onPageChange(p)}
            size="small"
          />
        </Box>
      )}
    </Box>
  );
}
